import React, { useContext, useState } from "react";
import { Table } from "react-bootstrap";
import classes from "./DataModal.module.css";
import PrescriptionPrint from "../Pages/Prescriptions/PrescriptionPrint";
import WaitingContext from "../../store/waitingListContext";
import AuthContext from "../../store/auth-context";
import { exportPrescriptionPDF } from "../../util/pdfExportUtil";
import { getAuthToken } from "../../util/auth";
import { getApiChara } from "../../util/getApi";
import { FaCheck, FaDownload, FaPrint, FaCalendarAlt, FaTimes, FaTrash, FaWeight, FaHeartbeat, FaThermometerHalf, FaLungs, FaRuler, FaBaby, FaCalendarDay, FaUserNurse } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ConfirmModal from "./ConfirmModal";

const FullHistoryModel = ({ allHistoryData, OnCloseModalBlock, onGetHistoryModelData, selectedDateTime, onDeleteSuccess }) => {
  const { t } = useTranslation();
  const checkPatient = useContext(WaitingContext);
  const authCtx = useContext(AuthContext);
  const hospitaData = checkPatient?.waitingPatient;

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedCheckupId, setSelectedCheckupId] = useState(null);

  const formatDisplayDate = (dateVal) => {
    if (!dateVal) return "";
    if (Array.isArray(dateVal)) {
      const [y, m, d] = dateVal;
      return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
    }
    return dateVal;
  };

  const handleExport = (selectedPatient) => {
    exportPrescriptionPDF({
      patientData: { ...hospitaData, checkUpId: selectedPatient?.checkUpId }, // Merge correct checkUpId
      medicines: selectedPatient.prescription,
      checkedData: {
        diagnosis: selectedPatient.diagnosis,
        weight: selectedPatient.weight,
        bps: selectedPatient.bp1,
        bpd: selectedPatient.bp2,
        pulse: selectedPatient.pulse,
        temp: selectedPatient.temperature,
        spo2: selectedPatient.spo2,
        complaint: selectedPatient.complaint,
        advice: selectedPatient.advice,
        notes: selectedPatient.notes,
        lmp: selectedPatient.lmp || selectedPatient.Lmp || "",
        edd: selectedPatient.edd || selectedPatient.Edd || "",
        hc: selectedPatient.hc || "",
        length: selectedPatient.length || "",
        nextAppointment: selectedPatient.nextAppointment || selectedPatient.next_appointment || ""
      },
      authCtx: authCtx,
      selectedDateTime: selectedPatient.dateTime,
      isHistory: true
    });
  };

  const onSelectHistoryData = (prescription) => {
    onGetHistoryModelData(prescription);
    OnCloseModalBlock();
  };

  const handleDeleteHistory = async () => {
    if (!selectedCheckupId) return;

    const token = getAuthToken();
    const apiChar = getApiChara();
    const apiUrl = `${apiChar}/checkUp/delete/${selectedCheckupId}`;

    try {
      const response = await fetch(apiUrl, {
        method: "DELETE",
        headers: {
          Authorization: "Bearer " + token,
        },
      });

      if (!response.ok) {
        throw new Error("Could not delete record");
      }

      toast.success(t("Medical history deleted successfully!"));
      if (onDeleteSuccess) {
        onDeleteSuccess();
      }
    } catch (error) {
      console.error("Error deleting history:", error);
      toast.error(t("Failed to delete medical history"));
    } finally {
      setShowDeleteConfirm(false);
      setSelectedCheckupId(null);
    }
  };

  const openDeleteConfirm = (id) => {
    setSelectedCheckupId(id);
    setShowDeleteConfirm(true);
  };

  return (
    <div>
      <div className={classes.backdrop} onClick={OnCloseModalBlock} />
      <div className={classes.modal}>
        <header className={classes.header}>
          <div className="d-flex justify-content-between align-items-center w-100 px-3">
            <h3 className="mb-0 text-white">{t("Medical History")}</h3>
            <button className={classes.closeButton} onClick={OnCloseModalBlock} title={t("Close")}>
              <FaTimes />
            </button>
          </div>
        </header>

        <div className={classes.content} style={{ padding: '15px', backgroundColor: '#f8f9fa' }}>
          {!allHistoryData || allHistoryData.length === 0 ? (
            <div className="text-center py-5 text-muted">{t("No history found")}</div>
          ) : (
            <div id="historyListContainer">
              {allHistoryData
                .sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime))
                .map((selectedPatient, index) => {
                  const checkedData = {
                    weight: selectedPatient?.weight || "",
                    bps: selectedPatient?.bp1 || "",
                    bpd: selectedPatient?.bp2 || "",
                    pulse: selectedPatient?.pulse || "",
                    temp: selectedPatient?.temperature || "",
                    spo2: selectedPatient?.spo2 || "",
                    complaint: selectedPatient?.complaint || "",
                    advice: selectedPatient?.advice || "",
                    allergy: selectedPatient?.allergy || "",
                    diagnosis: selectedPatient?.diagnosis || "",
                  };

                  const prescriptionCount = selectedPatient.prescription ? selectedPatient.prescription.length : 0;

                  return (
                    <div className="mb-4 border shadow-sm rounded-4 overflow-hidden bg-white" key={index}>
                      {/* Condensed Header */}
                      <div
                        className="d-flex w-100 justify-content-between align-items-center flex-wrap gap-2 history-card-header"
                        style={{
                          backgroundColor: 'var(--background-color)',
                          color: 'var(--secondary-color)',
                          padding: '0.6rem 1.25rem',
                          borderBottom: '1px solid var(--light-color)'
                        }}
                      >
                        <style>
                          {`
                            @media (max-width: 768px) {
                              .history-actions {
                                width: 100%;
                                justify-content: flex-start !important;
                                flex-wrap: wrap;
                              }
                            }
                          `}
                        </style>
                        <span className="d-flex align-items-center gap-2 fw-bold small">
                          <span style={{ color: 'var(--primary-color)' }}><FaCalendarAlt /></span>
                          {selectedPatient?.dateTime || "Unknown Date"}
                        </span>

                        <div className="d-flex align-items-center gap-2 history-actions">
                          <button
                            className="btn btn-sm rounded-pill shadow-sm d-flex align-items-center gap-2"
                            onClick={() => onSelectHistoryData(selectedPatient.prescription)}
                            style={{
                              backgroundColor: 'transparent',
                              border: '1px solid var(--primary-color)',
                              color: 'var(--primary-color)',
                              fontWeight: '600',
                              fontSize: '0.75rem',
                              padding: '4px 12px'
                            }}
                          >
                            <FaCheck size={10} /> {t("Select")}
                          </button>

                          <div style={{ display: 'inline-block' }}>
                            <PrescriptionPrint
                              patientData={{ ...hospitaData, checkUpId: selectedPatient.checkUpId }}
                              medicines={selectedPatient.prescription}
                              hospitaData={hospitaData}
                              checkedData={checkedData}
                              selectedDateTime={selectedDateTime}
                              isHistory={true}
                              className="btn btn-sm rounded-pill shadow-sm d-flex align-items-center gap-2"
                              style={{
                                backgroundColor: 'transparent',
                                border: '1px solid #6c757d',
                                color: '#6c757d',
                                fontWeight: '600',
                                fontSize: '0.75rem',
                                padding: '4px 12px'
                              }}
                            >
                              <FaPrint size={10} /> {t("Print")}
                            </PrescriptionPrint>
                          </div>

                          <button
                            className="btn btn-sm rounded-pill shadow-sm d-flex align-items-center gap-2"
                            onClick={() => handleExport(selectedPatient)}
                            style={{
                              backgroundColor: 'transparent',
                              border: '1px solid #6c757d',
                              color: '#6c757d',
                              fontWeight: '600',
                              fontSize: '0.75rem',
                              padding: '4px 12px'
                            }}
                          >
                            <FaDownload size={10} /> {t("PDF")}
                          </button>

                          <button
                            className="btn btn-sm rounded-pill shadow-sm d-flex align-items-center gap-2"
                            onClick={() => openDeleteConfirm(selectedPatient.checkUpId)}
                            style={{
                              backgroundColor: 'transparent',
                              border: '1px solid #dc3545',
                              color: '#dc3545',
                              fontWeight: '600',
                              fontSize: '0.75rem',
                              padding: '4px 12px'
                            }}
                            title={t("Delete")}
                          >
                            <FaTrash size={10} /> {t("Delete")}
                          </button>

                          <span className="badge bg-white border rounded-pill px-2 py-1 ms-2 small fw-bold" style={{ fontSize: '0.7rem', color: 'var(--primary-color)', borderColor: 'var(--primary-color) !important' }}>
                            {prescriptionCount} {t("Meds")}
                          </span>
                        </div>
                      </div>

                      {/* Card Content */}
                      <div className="p-3">
                        <div className={classes.vitalsGrid}>
                          {selectedPatient?.weight && (
                            <div className={classes.vitalItem}>
                              <FaWeight className={classes.vitalIcon} />
                              <span className={classes.vitalLabel}>{t("Wt")}</span>
                              <span className={classes.vitalValue}>{selectedPatient.weight}</span>
                            </div>
                          )}
                          {(selectedPatient?.bp1 || selectedPatient?.bp2) && (
                            <div className={classes.vitalItem}>
                              <FaUserNurse className={classes.vitalIcon} />
                              <span className={classes.vitalLabel}>{t("BP")}</span>
                              <span className={classes.vitalValue}>{selectedPatient?.bp1 || "-"}/{selectedPatient?.bp2 || "-"}</span>
                            </div>
                          )}
                          {selectedPatient?.pulse && (
                            <div className={classes.vitalItem}>
                              <FaHeartbeat className={classes.vitalIcon} />
                              <span className={classes.vitalLabel}>{t("Ps")}</span>
                              <span className={classes.vitalValue}>{selectedPatient.pulse}</span>
                            </div>
                          )}
                          {selectedPatient?.temperature && (
                            <div className={classes.vitalItem}>
                              <FaThermometerHalf className={classes.vitalIcon} />
                              <span className={classes.vitalLabel}>{t("Tp")}</span>
                              <span className={classes.vitalValue}>{selectedPatient.temperature}</span>
                            </div>
                          )}
                          {selectedPatient?.spo2 && (
                            <div className={classes.vitalItem}>
                              <FaLungs className={classes.vitalIcon} />
                              <span className={classes.vitalLabel}>{t("SpO2")}</span>
                              <span className={classes.vitalValue}>{selectedPatient.spo2}</span>
                            </div>
                          )}
                          {selectedPatient?.length && (
                            <div className={classes.vitalItem}>
                              <FaRuler className={classes.vitalIcon} />
                              <span className={classes.vitalLabel}>{t("Len")}</span>
                              <span className={classes.vitalValue}>{selectedPatient.length}</span>
                            </div>
                          )}
                          {selectedPatient?.hc && (
                            <div className={classes.vitalItem}>
                              <FaBaby className={classes.vitalIcon} />
                              <span className={classes.vitalLabel}>{t("HC")}</span>
                              <span className={classes.vitalValue}>{selectedPatient.hc}</span>
                            </div>
                          )}
                          {(selectedPatient?.lmp || selectedPatient?.Lmp) && (
                            <div className={classes.vitalItem}>
                              <FaCalendarDay className={classes.vitalIcon} />
                              <span className={classes.vitalLabel}>{t("LMP")}</span>
                              <span className={classes.vitalValue}>{formatDisplayDate(selectedPatient.lmp || selectedPatient.Lmp)}</span>
                            </div>
                          )}
                          {(selectedPatient?.edd || selectedPatient?.Edd) && (
                            <div className={classes.vitalItem}>
                              <FaCalendarDay className={classes.vitalIcon} />
                              <span className={classes.vitalLabel}>{t("EDD")}</span>
                              <span className={classes.vitalValue}>{formatDisplayDate(selectedPatient.edd || selectedPatient.Edd)}</span>
                            </div>
                          )}
                        </div>

                        {/* Details Grid - Compact Layered Layout */}
                        <div className="py-2">
                          <div className="row g-2">
                            {/* Row 1: Primary Clinical Values */}
                            {selectedPatient?.diagnosis && (
                              <div className="col-md-4 text-start">
                                <div className="p-2 border rounded bg-light h-100 shadow-sm border-light-subtle">
                                  <span className="fw-bold text-secondary d-block mb-1" style={{ fontSize: '0.65rem' }}>{t("Diagnosis")}</span>
                                  <span className="fw-medium small text-dark">{selectedPatient.diagnosis}</span>
                                </div>
                              </div>
                            )}
                            {selectedPatient?.complaint && (
                              <div className="col-md-4 text-start">
                                <div className="p-2 border rounded bg-light h-100 shadow-sm border-light-subtle">
                                  <span className="fw-bold text-secondary d-block mb-1" style={{ fontSize: '0.65rem' }}>{t("Complaint")}</span>
                                  <span className="text-dark small">{selectedPatient.complaint}</span>
                                </div>
                              </div>
                            )}
                            {selectedPatient?.advice && (
                              <div className="col-md-4 text-start">
                                <div className="p-2 border rounded bg-info-subtle border-light-subtle h-100 shadow-sm">
                                  <span className="fw-bold text-info-emphasis d-block mb-1" style={{ fontSize: '0.65rem' }}>{t("Advice")}</span>
                                  <span className="text-dark small">{selectedPatient.advice}</span>
                                </div>
                              </div>
                            )}

                            {/* Row 2: Secondary / Long Form Content */}
                            {selectedPatient?.notes && (
                              <div className="col-12 text-start">
                                <div className="p-2 border rounded bg-light shadow-sm border-light-subtle">
                                  <span className="fw-bold text-secondary d-block mb-1" style={{ fontSize: '0.65rem' }}>{t("Notes")}</span>
                                  <span className="text-dark small" style={{ whiteSpace: 'pre-wrap' }}>{selectedPatient.notes}</span>
                                </div>
                              </div>
                            )}

                            {/* Row 3: Admin / Alert Content */}
                            {selectedPatient?.allergy && (
                              <div className="col-md-6 text-start">
                                <div className="p-2 border rounded bg-danger-subtle border-danger-subtle shadow-sm h-100">
                                  <span className="fw-bold text-danger d-block mb-1" style={{ fontSize: '0.65rem' }}>{t("Allergy")}</span>
                                  <span className="text-danger fw-medium small">{selectedPatient.allergy}</span>
                                </div>
                              </div>
                            )}
                            {(selectedPatient?.nextAppointment || selectedPatient?.next_appointment) && (
                              <div className="col-md-6 text-start">
                                <div className="p-2 border rounded bg-warning-subtle border-warning-subtle shadow-sm h-100">
                                  <span className="fw-bold text-warning-emphasis d-block mb-1" style={{ fontSize: '0.65rem' }}>{t("Next Appointment")}</span>
                                  <span className="fw-bold small text-dark">{formatDisplayDate(selectedPatient.nextAppointment || selectedPatient.next_appointment)}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Highly Compact Medicine Table */}
                        {selectedPatient.prescription && selectedPatient.prescription.length > 0 && (
                          <div className="mt-2 border rounded overflow-hidden">
                            <Table hover responsive className="mb-0 overflow-hidden align-middle">
                              <thead>
                                <tr style={{ backgroundColor: '#f1f5f9', fontSize: '0.75rem', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>
                                  <th className="ps-3 py-2 border-0" style={{ color: 'var(--primary-color)', fontWeight: '800', letterSpacing: '0.5px' }}>{t("Medicine Name")}</th>
                                  <th className="py-2 border-0" style={{ color: 'var(--primary-color)', fontWeight: '800', letterSpacing: '0.5px' }}>{t("Dosage")}</th>
                                  <th className="py-2 border-0" style={{ color: 'var(--primary-color)', fontWeight: '800', letterSpacing: '0.5px' }}>{t("Type")}</th>
                                  <th className="py-2 border-0 text-center" style={{ color: 'var(--primary-color)', fontWeight: '800', letterSpacing: '0.5px' }}>{t("B/L/D")}</th>
                                  <th className="py-2 border-0 text-center" style={{ color: 'var(--primary-color)', fontWeight: '800', letterSpacing: '0.5px' }}>{t("Days")}</th>
                                  <th className="py-2 border-0" style={{ color: 'var(--primary-color)', fontWeight: '800', letterSpacing: '0.5px' }}>{t("Instruction")}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedPatient.prescription.map((medicine, idx) => (
                                  <tr key={idx} className="border-top">
                                    <td className="ps-3 py-2 fw-bold text-dark" style={{ fontSize: '0.8rem' }}>{medicine?.medicineName || ""}</td>
                                    <td className="py-2 fw-bold text-primary" style={{ fontSize: '0.75rem' }}>{medicine?.dosage || "-"}</td>
                                    <td className="py-2 text-secondary" style={{ fontSize: '0.75rem' }}>{medicine?.type || ""}</td>
                                    <td className="py-2 text-muted text-center" style={{ fontSize: '0.75rem' }}>
                                      {medicine?.breakFastDose || "-"}/{medicine?.lunchDose || "-"}/{medicine?.dinnerDose || "-"}
                                    </td>
                                    <td className="py-2 fw-bold text-center" style={{ fontSize: '0.75rem', color: 'var(--primary-color)' }}>{medicine?.days || ""}</td>
                                    <td className="py-2 text-muted fst-italic" style={{ fontSize: '0.75rem' }}>{medicine?.instruction || ""}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              }
            </div>
          )}
        </div>

        <footer className={classes.actions}>
          <button onClick={OnCloseModalBlock} className="btn btn-sm px-4 fw-bold" style={{ backgroundColor: 'var(--primary-color)', color: 'white', border: 'none' }}>{t("Close")}</button>
        </footer>
      </div>

      <ConfirmModal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteHistory}
        title={t("Delete History")}
        message={t("Are you sure you want to delete this medical history?")}
        variant="danger"
        confirmText={t("Delete")}
      />
    </div>
  );
};

export default FullHistoryModel;
